import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, RotateCcw, Loader2, Calendar, MapPin, Users, Lock, ScrollText } from "lucide-react";
import SignaturePad from "../components/SignaturePad";
import { api, API as API_BASE } from "../lib/api";

/**
 * PUBLIC meeting sign-in screen.
 *
 * Designed to run on a shared tablet at the meeting room. Every attendee:
 *   1. Types their full name.
 *   2. (Optional) Selects their role.
 *   3. Signs the canvas.
 *   4. Taps "Sign in".
 * After a 2s confirmation, the form resets ready for the next attendee.
 */
const ROLES = [
  { v: "trustee", label: "Trustee" },
  { v: "chair", label: "Chair" },
  { v: "secretary", label: "Secretary" },
  { v: "treasurer", label: "Treasurer" },
  { v: "dsl", label: "DSL" },
  { v: "education_lead", label: "Education Lead" },
  { v: "observer", label: "Observer / Guest" },
];

export default function MeetingSignIn() {
  const { meeting_id } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState("trustee");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const sigRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await api.get(`/meetings/${meeting_id}/info`);
      setMeeting(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Meeting not found");
    }
  };
  useEffect(() => { refresh(); }, [meeting_id]);

  // Auto-refresh attendee count every 8s
  useEffect(() => {
    if (!meeting) return;
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, [meeting?.meeting_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setErr("Please enter your full name.");
    if (sigRef.current?.isEmpty()) return setErr("Please draw your signature.");
    setBusy(true); setErr(null);
    try {
      await api.post(`/meetings/${meeting_id}/signin`, {
        full_name: name.trim(),
        role,
        signature_dataurl: sigRef.current.toDataURL(),
      });
      setDone(true);
      setTimeout(() => {
        setDone(false);
        setName("");
        setRole("trustee");
        sigRef.current?.clear();
        refresh();
      }, 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  if (err && !meeting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pearl p-6">
        <div className="text-center">
          <h1 className="font-display text-2xl text-navy">Meeting not found</h1>
          <p className="mt-2 text-sm text-slate-500">{err}</p>
        </div>
      </div>
    );
  }
  if (!meeting) {
    return <div className="min-h-screen flex items-center justify-center bg-pearl"><Loader2 className="animate-spin text-saffron" /></div>;
  }

  const closed = meeting.status !== "open";

  return (
    <div className="min-h-screen bg-pearl" data-testid="meeting-signin-page">
      {/* Header */}
      <header className="text-pearl py-8 px-6" style={{ background: "linear-gradient(135deg,#0B1F4D 0%,#CFA052 130%)" }}>
        <div className="max-w-3xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.3em] text-saffron flex items-center gap-2">
            <ScrollText size={12} /> Trustee meeting sign-in · ABGN-UK
          </p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl">{meeting.title}</h1>
          <div className="mt-3 flex flex-wrap gap-4 text-sm text-pearl/80">
            <span className="inline-flex items-center gap-1"><Calendar size={14}/> {meeting.scheduled_at}</span>
            {meeting.location && <span className="inline-flex items-center gap-1"><MapPin size={14}/> {meeting.location}</span>}
            <span className="inline-flex items-center gap-1"><Users size={14}/> {meeting.attendee_count} signed in</span>
            {closed && <span className="inline-flex items-center gap-1 text-red-200"><Lock size={14}/> Sign-in closed</span>}
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {closed ? (
          <div className="bg-white border border-navy/10 rounded-sm p-10 text-center" data-testid="meeting-closed">
            <Lock size={36} className="mx-auto text-navy/40" />
            <h2 className="mt-4 font-display text-2xl text-navy">This meeting is closed</h2>
            <p className="mt-2 text-sm text-slate-500">Sign-in is no longer being accepted. Speak to the meeting chair if this is unexpected.</p>
          </div>
        ) : done ? (
          <div className="bg-white border border-navy/10 rounded-sm p-10 text-center" data-testid="meeting-signin-done">
            <CheckCircle2 size={48} className="mx-auto text-green-600" />
            <h2 className="mt-4 font-display text-2xl text-navy">Welcome, {name.split(" ")[0]} — you're signed in.</h2>
            <p className="mt-2 text-sm text-slate-500">Pass the device to the next attendee.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-navy/10 rounded-sm p-6 space-y-5" data-testid="meeting-signin-form">
            <h2 className="font-display text-xl text-navy">Sign in to this meeting</h2>

            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Your full name <span className="text-red-500">*</span></span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="e.g. Aarti Patel"
                data-testid="ms-name"
                className="w-full border border-navy/15 rounded-sm px-3 py-3 text-base"
              />
            </label>

            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Your role at the meeting</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="ms-role"
                className="w-full border border-navy/15 rounded-sm px-3 py-3 text-base"
              >
                {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
            </label>

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Signature <span className="text-red-500">*</span></span>
                <button type="button" onClick={() => sigRef.current?.clear()} className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1">
                  <RotateCcw size={11} /> Clear
                </button>
              </div>
              <SignaturePad ref={sigRef} testid="ms-sig" height={170} />
            </div>

            {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm" data-testid="ms-error">{err}</p>}

            <button
              type="submit"
              disabled={busy}
              data-testid="ms-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-saffron text-navy rounded-sm text-base font-medium hover:bg-saffron-light disabled:opacity-50"
            >
              {busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
              Sign in
            </button>
            <p className="text-[11px] text-slate-400 text-center">
              Your signature is stored only for the purpose of attendance records. ABGN-UK retains attendance
              records for 7 years in line with Charity Commission expectations.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
